import React, { useState } from "react";

import useCreateCustomer from "@src/hooks/useCreateCustomer";
import { InputField, CheckboxField, renderFieldOrSelect } from "@stories/ecommerce/Account/AccountFoodClubFields";

const REQUIRED_FIELDS = ['phone', 'email'];
const EMPTY_KEYS = REQUIRED_FIELDS.reduce((acc, key) => {
  acc[key] = "";
  return acc;
}, {});

const NewsletterRegisterForm = ({ settings }) => {

  const { section, signupfields, translations } = settings;
  const { createCustomer, loading, error, customerId, success } = useCreateCustomer();
  const [dependenciesState, setDependenciesState]: any = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [errorKeys, setErrorKeys] = useState(EMPTY_KEYS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBusinessFields, setShowBusinessFields] = useState(false);

  const customSignupFields = signupfields?.filter(item => item.show_on_newsletter_form).sort((a, b) => {
    const orderA = a.register_form_order ? parseInt(a.register_form_order) : Infinity;
    const orderB = b.register_form_order ? parseInt(b.register_form_order) : Infinity;
    return orderA - orderB;
  });

  // Handle input change and update global dependency state
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    // For checkboxes, use the checked value, otherwise use the input value
    const newValue = inputType === "checkbox" ? checked : value;

    // Update the global dependency state
    updateDependencyState(name, newValue);
  };

  // Function to update the global dependencies state
  const updateDependencyState = (name, value) => {
    setDependenciesState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleValidation = () => {
    const detectMissingKeys = (dependenciesState) => {
      // Filter to find missing or empty fields
      const missingKeys = REQUIRED_FIELDS.filter((key) => {
        if (key === 'phone' && !dependenciesState?.optInSMS) return false;

        return !(key in dependenciesState) || dependenciesState[key] === "";
      });

      return missingKeys;
    };

    const missingKeys = detectMissingKeys(dependenciesState);

    if (missingKeys.length > 0) {
      setErrorKeys((prevState) => {

        const updatedErrorKeys = missingKeys.reduce((acc, key) => {
          acc[key] = "Please fill required field";
          return acc;
        }, {});

        Object.keys(prevState).forEach((key) => {
          if (!missingKeys.includes(key)) {
            updatedErrorKeys[key] = "";
          }
        });

        return { ...prevState, ...updatedErrorKeys };
      });
      setErrorMessage("Please fill required field(s)");
      return false;
    } else {
      setErrorKeys(EMPTY_KEYS)
      setErrorMessage("");
      return true;
    }
  }

  const handleClick = async (event) => {
    event.preventDefault();
    const success = await handleValidation();

    if (!success) return;

    setIsSubmitting(true);
    setDependenciesState((prevState) => ({
      ...prevState,
      ...(prevState.optInSMS === undefined && { optInSMS: false }),
      ...(prevState.optInEmail === undefined && { optInEmail: false }),
    }));

    const createResponse = await createCustomer(dependenciesState);

    if (createResponse) {
      // const email = dependenciesState.email;
      // const password = dependenciesState.password;

      // Log the customer in after successful creation
      // await loginCustomer(email, password);
    }

    setIsSubmitting(false);
  };


  return (
    <form
      id="newsletterRegisterForm"
      className="newsletter-sign-up-form flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <InputField id="name" type="text" placeholder={`Name`} required handleInputChange={handleInputChange} />
          {(errorKeys['name'] != '') &&
            <div className='text-mb-red text-xs pl-3;'>
              {errorKeys['name']}
            </div>
          }
        </div>

        <div>
          <InputField id="email" type="email" required placeholder={`Email`} handleInputChange={handleInputChange} />
          {(errorKeys['email'] != '') &&
            <div className='text-mb-red text-xs pl-3;'>
              {errorKeys['email']}
            </div>
          }
        </div>

        {showBusinessFields && 
          <>
            <div>
              <InputField id="phone" type="tel" required={dependenciesState.optInSMS} placeholder={`Phone Number`} handleInputChange={handleInputChange} />
              {(errorKeys['phone'] != '') &&
                <div className='text-mb-red text-xs pl-3;'>
                  {errorKeys['phone']}
                </div>
              }
            </div>

            {customSignupFields.map((field, index) => renderFieldOrSelect(field, handleInputChange, index, false))}
          </>
        }
      </div>

      {/* Checkboxes */}
      <div className="account-page__checkboxes flex max-lg:flex-col gap-4">
        <CheckboxField
          label="Are you a business?"
          id="isBusiness"
          onChange={(e) => setShowBusinessFields(e.target.checked)}
        />

        <div onClick={handleClick} className={`account-page__submit-btn button button-${section.cta_button_style} w-full flex-1`}>
          {isSubmitting || loading ? "Submitting..." : "Sign Up"}
        </div>
      </div>

      <p
        className="terms-and-conditions text-xs montserrat-regular"
        dangerouslySetInnerHTML={{ __html: translations.terms_and_condition_html }}
      ></p>

      <div className="account-page__checkboxes flex max-lg:flex-col gap-4">
        <CheckboxField label="Opt in to receive SMS" id="optInSMS" onChange={handleInputChange} />
        <CheckboxField label="Opt in to receive Email" id="optInEmail" onChange={handleInputChange} />
      </div>

      {success && (
        <div
          className="success-message py-[16px]  px-4"
          role="alert"
        >
          <p className="b3">
            {translations.success}
          </p>
        </div>
      )}

      {error && (
        <div className="error-message py-[16px] bg-mb-red-light border-mb-red text-mb-black px-4" role="alert">
          <p>{error}</p>
        </div>
      )}

      {errorMessage && (
        <div className="error-message py-[16px] bg-mb-red-light border-mb-red text-mb-black px-4" role="alert">
          <p>{errorMessage}</p>
        </div>
      )}
    </form>
  );
};

export default NewsletterRegisterForm;
